import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useApi } from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { formatDistanceToNow } from 'date-fns';
import LoadingIndicator from '../components/LoadingIndicator';

export default function RecordingsPage() {
  const { execute, loading } = useApi();
  const { showSuccess, showError } = useToast();
  const { theme } = useTheme();

  // State management
  const [recordings, setRecordings] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    includeDeleted: false,
    conversationId: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadingStats, setLoadingStats] = useState(true);

  // Fetch recordings data
  const fetchRecordings = async (page = 1, customFilters = {}) => {
    try {
      const response = await execute(async (apiClient) => {
        const params = {
          page,
          limit: 20,
          ...customFilters
        };

        // Add search query if exists
        if (searchQuery) {
          params.search = searchQuery;
        }

        return await apiClient.get('/api/call-recordings', { params });
      });

      if (response.success) {
        setRecordings(response.data || []);
        setPagination(response.pagination);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Failed to fetch recordings:', error);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      setLoadingStats(true);
      const response = await execute(async (apiClient) => {
        return await apiClient.get('/api/call-recordings/statistics');
      });

      if (response.success) {
        setStatistics(response.statistics);
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Handle soft delete
  const handleSoftDelete = async (recording) => {
    try {
      const response = await execute(async (apiClient) => {
        return await apiClient.delete(`/api/call-recordings/${recording.conversation_id}`);
      });

      if (response.success) {
        showSuccess('Call recording deleted successfully');
        await fetchRecordings(currentPage, filters);
        await fetchStatistics();
      }
    } catch (error) {
      showError('Failed to delete call recording');
    }
  };

  // Handle file download
  const handleDownload = async (recordingId, fileType) => {
    try {
      const response = await execute(async (apiClient) => {
        return await apiClient.get(`/api/call-recordings/${recordingId}/download`, {
          params: { type: fileType }
        });
      });

      if (response.success && response.downloadUrl) {
        // Create a temporary link to download the file
        const link = document.createElement('a');
        link.href = response.downloadUrl;
        link.download = `${recordingId}-${fileType}.${fileType === 'audio' ? 'wav' : fileType === 'transcript' ? 'txt' : 'raw'}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      showError('Failed to download file');
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchRecordings();
    fetchStatistics();
  }, []);

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    fetchRecordings(1, updatedFilters);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecordings(1, filters);
  };

  // Format duration
  const formatDuration = (durationMs) => {
    if (!durationMs) return 'N/A';
    const seconds = Math.round(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Format file size
  const formatFileSize = (sizeKB) => {
    if (!sizeKB) return 'N/A';
    if (sizeKB < 1024) {
      return `${sizeKB} KB`;
    }
    return `${(sizeKB / 1024).toFixed(1)} MB`;
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Call Recordings</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Listen to call recordings, view transcripts, and access raw call logs
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {loadingStats ? (
          <div className="col-span-4">
            <LoadingIndicator type="spinner" size="small" />
          </div>
        ) : statistics ? (
          <>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Recordings</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {statistics.total_recordings || 0}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Audio Files</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {statistics.audio_files || 0}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Duration</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatDuration(statistics.total_duration_ms)}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Storage Used</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {statistics.storage_estimate?.total_size_mb || 0} MB
              </div>
            </div>
          </>
        ) : (
          <div className="col-span-4 text-center text-gray-600 dark:text-gray-400">
            No statistics available
          </div>
        )}
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6 border border-gray-200 dark:border-gray-700">
        <form onSubmit={handleSearch} className="space-y-4">
          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search recordings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <LoadingIndicator type="spinner" size="small" /> : 'Search'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setFilters({ includeDeleted: false, conversationId: '' });
                  fetchRecordings(1, {});
                }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeDeleted"
                checked={filters.includeDeleted}
                onChange={(e) => handleFilterChange({ includeDeleted: e.target.checked })}
                className="rounded text-teal-600 focus:ring-teal-500"
              />
              <label htmlFor="includeDeleted" className="text-sm text-gray-700 dark:text-gray-300">
                Include deleted recordings
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Conversation ID"
                value={filters.conversationId}
                onChange={(e) => handleFilterChange({ conversationId: e.target.value })}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        </form>
      </div>

      {/* Recordings List */}
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-700">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingIndicator type="spinner" size="large" />
          </div>
        ) : recordings.length === 0 ? (
          <div className="text-center py-12 text-gray-600 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
            <h3 className="text-lg font-medium mb-2">No recordings found</h3>
            <p className="text-sm">Your call recordings will appear here once you have conversations.</p>
          </div>
        ) : (
          <ul role="list" className="divide-y divide-gray-200 dark:divide-gray-700">
            {recordings.map((recording) => (
              <li key={recording.id || recording.conversation_id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <p className="text-sm font-medium text-teal-600 dark:text-teal-400 truncate">
                          {recording.conversation_id || `Recording ${recording.id}`}
                        </p>
                        {recording.parrot_name && (
                          <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 rounded">
                            {recording.parrot_name}
                          </span>
                        )}
                        {recording.is_deleted && (
                          <span className="px-2 py-1 text-xs bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-100 rounded">
                            Deleted
                          </span>
                        )}
                      </div>

                      {/* Timing Information */}
                      {recording.total_turns > 0 || recording.avg_stt_time_ms > 0 ? (
                        <div className="flex flex-wrap gap-4 mb-3 text-xs text-gray-600 dark:text-gray-400">
                          {recording.total_turns > 0 && (
                            <span>{recording.total_turns} turns</span>
                          )}
                          {recording.avg_stt_time_ms > 0 && (
                            <span>STT: {(recording.avg_stt_time_ms || 0).toFixed(0)}ms avg</span>
                          )}
                          {recording.avg_llm_time_ms > 0 && (
                            <span>LLM: {(recording.avg_llm_time_ms || 0).toFixed(0)}ms avg</span>
                          )}
                          {recording.avg_tts_time_ms > 0 && (
                            <span>TTS: {(recording.avg_tts_time_ms || 0).toFixed(0)}ms avg</span>
                          )}
                        </div>
                      ) : null}

                      {/* File Availability */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {recording.audio_file_path && (
                          <button
                            onClick={() => handleDownload(recording.conversation_id || recording.id, 'audio')}
                            className="px-3 py-1 text-xs bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-100 rounded hover:bg-green-200 dark:hover:bg-green-700"
                            title="Download audio file"
                          >
                            🎵 Audio
                          </button>
                        )}
                        {recording.transcript_file_path && (
                          <button
                            onClick={() => handleDownload(recording.conversation_id || recording.id, 'transcript')}
                            className="px-3 py-1 text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 rounded hover:bg-blue-200 dark:hover:bg-blue-700"
                            title="Download transcript"
                          >
                            📄 Transcript
                          </button>
                        )}
                        {recording.raw_log_file_path && (
                          <button
                            onClick={() => handleDownload(recording.conversation_id || recording.id, 'raw_log')}
                            className="px-3 py-1 text-xs bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-100 rounded hover:bg-purple-200 dark:hover:bg-purple-700"
                            title="Download raw logs"
                          >
                            📊 Raw Logs
                          </button>
                        )}
                      </div>

                      {/* Date and Duration */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                          <svg className="flex-shrink-0 mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {recording.created_at
                            ? formatDistanceToNow(new Date(recording.created_at), { addSuffix: true })
                            : 'Unknown date'
                          }
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                            {formatDuration(recording.total_duration_ms)}
                          </span>
                          {!recording.is_deleted && (
                            <button
                              onClick={() => handleSoftDelete(recording)}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete recording"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Showing {((currentPage - 1) * pagination.limit) + 1} to {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} results
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fetchRecordings(currentPage - 1, filters)}
              disabled={!pagination.hasPrev}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-white"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm dark:text-gray-300">
              Page {currentPage} of {pagination.totalPages}
            </span>
            <button
              onClick={() => fetchRecordings(currentPage + 1, filters)}
              disabled={!pagination.hasNext}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-white"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}